/**
 * The engine's events, delivered onto the UI bus.
 *
 * One long-poll loop: ask `/api/events?since=N&wait=20`, publish what comes
 * back, ask again from the new sequence number. The engine holds the request
 * open until something happens, so an idle library costs one quiet request
 * every twenty seconds instead of the old pattern of half a dozen components
 * each polling on their own timer.
 *
 * If the engine reports a gap (we fell behind its buffer) or the connection
 * drops and comes back, `engine.resync` is published: listeners should treat
 * everything as changed and re-read once.
 */

import { bus } from "./bus";

interface EngineEvent {
  seq: number;
  topic: string;
  data: unknown;
  source: string;
}

/**
 * Which loop is the live one. Starting again retires the previous loop (it
 * stops after its current request) rather than refusing: React's StrictMode
 * starts, stops and restarts every effect in development, and refusing the
 * restart left the window with no feed at all.
 */
let generation = 0;

export function startEngineEvents(baseUrl: () => string): () => void {
  const mine = ++generation;
  const live = () => generation === mine;
  let since = -1;
  let failures = 0;

  const loop = async () => {
    while (live()) {
      try {
        const wait = since < 0 ? 0 : 20;
        const res = await fetch(`${baseUrl()}/api/events?since=${since}&wait=${wait}`);
        if (!res.ok) throw new Error(String(res.status));
        const body = (await res.json()) as { seq: number; gap: boolean; events: EngineEvent[] };
        if (!live()) break;
        if (failures > 0 || body.gap) bus.emit("engine.resync", { reason: body.gap ? "gap" : "reconnected" }, "engine");
        failures = 0;
        if (since >= 0) {
          for (const e of body.events) bus.emit(e.topic, e.data, e.source === "core" ? "engine" : e.source);
        } else {
          bus.emit("engine.connected", { seq: body.seq }, "engine");
        }
        since = body.seq;
      } catch {
        if (!live()) break;
        failures += 1;
        if (failures === 1) bus.emit("engine.disconnected", null, "engine");
        // Back off: 1 s, 2 s, 4 s … capped at 10 s, so a stopped engine is cheap.
        await new Promise((r) => setTimeout(r, Math.min(10_000, 1000 * 2 ** Math.min(failures - 1, 4))));
      }
    }
  };
  void loop();
  return () => {
    if (live()) generation += 1;
  };
}
